import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import axios from '../config/api';
import { useToast } from '../context/ToastContext';
import { formatCurrency } from '../utils/format';

interface ScannedUser {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  points: number;
  subscription_type: string | null;
  student_verified: boolean;
}

export default function QRScanner() {
  const [scanning, setScanning] = useState(false);
  const [scannedUser, setScannedUser] = useState<ScannedUser | null>(null);
  const [amount, setAmount] = useState('');
  const [items, setItems] = useState<Array<{ menu_item_id: string; quantity: number; note?: string }>>([]);
  const [pointsUsed, setPointsUsed] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [cafeId, setCafeId] = useState('');
  const [cafes, setCafes] = useState<any[]>([]);
  const [manualQRInput, setManualQRInput] = useState('');
  const [scanMode, setScanMode] = useState<'camera' | 'scanner'>('camera');
  const { showToast } = useToast();
  const html5QrCodeRef = useRef<Html5Qrcode | null>(null);
  const scannerId = 'qr-reader';
  const manualInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchCafes();
    return () => {
      // Cleanup scanner on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // Handle physical scanner input (keyboard events)
  useEffect(() => {
    if (scanMode === 'scanner' && manualInputRef.current) {
      const handleKeyPress = (e: KeyboardEvent) => {
        // Focus the input field when any key is pressed (scanner input)
        if (document.activeElement !== manualInputRef.current) {
          manualInputRef.current?.focus();
        }
      };

      window.addEventListener('keypress', handleKeyPress);
      return () => {
        window.removeEventListener('keypress', handleKeyPress);
      };
    }
  }, [scanMode]);

  // Auto-submit when QR code is scanned (physical scanners usually end with Enter)
  const handleManualQRSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const qrData = manualQRInput.trim();
    
    if (!qrData) {
      showToast('Please scan or enter a QR code', 'error');
      return;
    }

    await verifyQRCode(qrData);
    setManualQRInput(''); // Clear input after scanning
  };

  // Verify QR code (shared function for both camera and scanner)
  const verifyQRCode = async (qrData: string) => {
    try {
      const response = await axios.post('/api/qrcode/verify', {
        qrData: qrData,
      });

      if (response.data.success) {
        setScannedUser(response.data.user);
        showToast(`Scanned: ${response.data.user.name}`, 'success');
      } else {
        showToast('Invalid QR code', 'error');
      }
    } catch (error: any) {
      console.error('QR verification error:', error);
      showToast(error.response?.data?.error || 'Failed to verify QR code', 'error');
    }
  };

  const fetchCafes = async () => {
    try {
      const response = await axios.get('/api/cafes');
      setCafes(response.data);
      if (response.data.length > 0) {
        setCafeId(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching cafes:', error);
      showToast('Failed to load cafes', 'error');
    }
  };

  const startScanning = async () => {
    try {
      setScanning(true);
      setScannedUser(null);

      const html5QrCode = new Html5Qrcode(scannerId);
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        async (decodedText) => {
          // QR code scanned successfully
          await html5QrCode.stop();
          setScanning(false);
          html5QrCodeRef.current = null;

          // Verify QR code with backend
          await verifyQRCode(decodedText);
        },
        (errorMessage) => {
          // Ignore scanning errors (they're frequent during scanning)
        }
      );
    } catch (error: any) {
      console.error('Error starting scanner:', error);
      setScanning(false);
      showToast('Failed to start camera. Please check permissions.', 'error');
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    setScanning(false);
  };

  const handleProcessPayment = async () => {
    if (!scannedUser || !amount || !cafeId || items.length === 0) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      showToast('Please enter a valid amount', 'error');
      return;
    }

    if (pointsUsed > (scannedUser.points || 0)) {
      showToast('Customer does not have enough points', 'error');
      return;
    }

    setProcessing(true);

    try {
      // Get the QR data from the scanned user
      const qrData = `COFFEEAPP:USER:${scannedUser.id}`;

      const response = await axios.post('/api/qrcode/process-payment', {
        qrData,
        amount: amountNum,
        cafeId,
        items,
        pointsUsed,
      });

      if (response.data.success) {
        showToast(
          `Payment processed! Order #${response.data.orderNumber}. Points earned: ${response.data.pointsEarned}`,
          'success'
        );
        
        // Reset form
        setScannedUser(null);
        setAmount('');
        setItems([]);
        setPointsUsed(0);
      }
    } catch (error: any) {
      console.error('Payment processing error:', error);
      showToast(error.response?.data?.error || 'Failed to process payment', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const addItem = () => {
    setItems([...items, { menu_item_id: '', quantity: 1, note: '' }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-amber-800">QR Code Scanner</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scanner Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 text-amber-800">Scan Customer QR Code</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Select Cafe <span className="text-red-500">*</span>
            </label>
            <select
              value={cafeId}
              onChange={(e) => setCafeId(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Select a cafe</option>
              {cafes.map((cafe) => (
                <option key={cafe.id} value={cafe.id}>
                  {cafe.name}
                </option>
              ))}
            </select>
          </div>

          {/* Scan Mode Selection */}
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Scan Mode
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setScanMode('camera');
                  stopScanning();
                  setScannedUser(null);
                }}
                className={`flex-1 px-4 py-2 rounded transition font-semibold ${
                  scanMode === 'camera'
                    ? 'bg-amber-700 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                📷 Camera
              </button>
              <button
                onClick={() => {
                  setScanMode('scanner');
                  stopScanning();
                  setScannedUser(null);
                  setTimeout(() => manualInputRef.current?.focus(), 100);
                }}
                className={`flex-1 px-4 py-2 rounded transition font-semibold ${
                  scanMode === 'scanner'
                    ? 'bg-amber-700 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                🔍 Physical Scanner
              </button>
            </div>
          </div>

          {/* Camera Scanner Mode */}
          {scanMode === 'camera' && (
            <>
              <div id={scannerId} className="mb-4"></div>

              {!scanning && !scannedUser && (
                <button
                  onClick={startScanning}
                  disabled={!cafeId}
                  className="w-full bg-amber-700 text-white px-4 py-3 rounded hover:bg-amber-800 transition disabled:opacity-50 font-semibold"
                >
                  Start Camera Scanning
                </button>
              )}

              {scanning && (
                <button
                  onClick={stopScanning}
                  className="w-full bg-red-600 text-white px-4 py-3 rounded hover:bg-red-700 transition font-semibold"
                >
                  Stop Scanning
                </button>
              )}
            </>
          )}

          {/* Physical Scanner Mode */}
          {scanMode === 'scanner' && (
            <form onSubmit={handleManualQRSubmit} className="mb-4">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Scan QR Code with Physical Scanner
              </label>
              <input
                ref={manualInputRef}
                type="text"
                value={manualQRInput}
                onChange={(e) => setManualQRInput(e.target.value)}
                onKeyDown={(e) => {
                  // Auto-submit on Enter (most physical scanners send Enter after scanning)
                  if (e.key === 'Enter' && manualQRInput.trim()) {
                    handleManualQRSubmit(e);
                  }
                }}
                placeholder="Point scanner here and scan QR code..."
                className="w-full px-3 py-3 border-2 border-amber-500 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500 text-center font-mono text-lg"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Point your physical scanner at the QR code, or paste the code manually
              </p>
              <button
                type="submit"
                disabled={!manualQRInput.trim()}
                className="w-full mt-2 bg-amber-700 text-white px-4 py-2 rounded hover:bg-amber-800 transition disabled:opacity-50 font-semibold"
              >
                Verify QR Code
              </button>
            </form>
          )}

          {scannedUser && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-300 rounded">
              <h3 className="font-bold text-green-800 mb-2">Customer Scanned:</h3>
              <p className="text-sm text-gray-700">
                <strong>Name:</strong> {scannedUser.name}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Email:</strong> {scannedUser.email}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Points:</strong> {scannedUser.points || 0}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Subscription:</strong> {scannedUser.subscription_type || 'Regular'}
              </p>
              <button
                onClick={() => {
                  setScannedUser(null);
                  stopScanning();
                }}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Scan Different Customer
              </button>
            </div>
          )}
        </div>

        {/* Payment Form Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 text-amber-800">Process Payment</h2>

          {!scannedUser ? (
            <div className="text-center py-8 text-gray-500">
              <p>Please scan a customer QR code first</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Total Amount (JOD) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Points to Use (Optional)
                </label>
                <input
                  type="number"
                  value={pointsUsed}
                  onChange={(e) => setPointsUsed(Math.max(0, parseInt(e.target.value) || 0))}
                  max={scannedUser.points || 0}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Customer has {scannedUser.points || 0} points available
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Order Items <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={addItem}
                    className="text-sm text-amber-700 hover:text-amber-800 font-semibold"
                  >
                    + Add Item
                  </button>
                </div>
                {items.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Menu Item ID"
                      value={item.menu_item_id}
                      onChange={(e) => updateItem(index, 'menu_item_id', e.target.value)}
                      className="flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseInt(e.target.value) || 1)}
                      min="1"
                      className="w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />
                    <button
                      onClick={() => removeItem(index)}
                      className="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {items.length === 0 && (
                  <p className="text-sm text-gray-500 italic">No items added yet</p>
                )}
              </div>

              <button
                onClick={handleProcessPayment}
                disabled={processing || !amount || items.length === 0}
                className="w-full bg-amber-700 text-white px-4 py-3 rounded hover:bg-amber-800 transition disabled:opacity-50 font-semibold"
              >
                {processing ? 'Processing...' : 'Process Payment'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
